import dynamic from 'next/dynamic'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { has, pick } from 'ramda'

import { breakpoints } from '@/styles'
import { initializeApollo } from '@/lib/apollo'
import { FILTERS } from '@/lib/filters'
import { getListingData } from '@/lib/listing'
import { getListingsData, withCitiesQueryRedirect } from '@/lib/listings'
import { initializeStore } from '@/lib/redux'
import { getSession } from '@/lib/session'
import { makeMainLayout } from '@/layouts/main'
import ListingsMap from '@/features/ListingsMap'

const ListingDetailPage = dynamic(() => import('@/features/ListingDetail/page'))

const headerConfig = {
  showSearch: true
}
const footerConfig = {
  hideOnBreakpoint: breakpoints.ipadMiniMax
}
const supportConfig = {
  enabled: false
}

const Component = ({ isFilterPage }) => {
  return isFilterPage ? <ListingsMap /> : <ListingDetailPage />
}

const selectFromState = pick(['listing', 'session'])

const getListingsMapProps = async ({ data, reduxStore, apolloClient }) => {
  const { locale, query } = data
  const { redirect } = withCitiesQueryRedirect({ query })
  if (redirect) return { redirect }

  await getListingsData({ query, reduxStore, apolloClient })
  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      initialReduxState: selectFromState(reduxStore.getState()),
      ...(await serverSideTranslations(locale, [
        'translation',
        'seo',
        'states',
        'cities',
        'filters'
      ])),
      isFilterPage: true
    }
  }
}

const getListingDetailsProps = async ({ data, reduxStore }) => {
  const { req, params, locale } = data
  const [listing] = await Promise.all([
    getListingData({
      params,
      reduxStore,
      req
    }),
    getSession({ req, reduxStore })
  ])

  if (!listing) return { notFound: true }

  return {
    props: {
      initialReduxState: selectFromState(reduxStore.getState()),
      ...(await serverSideTranslations(locale, [
        'translation',
        'basic',
        'listing',
        'poi',
        'states',
        'cities'
      ]))
    }
  }
}

export const getServerSideProps = data => {
  // This route can be either a "Canada" map view filter page, or a listing details page
  // If the slug is a filter we show the "Canada" map view, otherwise we show listing details page
  const isFilterPage = has(data.query.slug, FILTERS)
  const reduxStore = initializeStore()
  const apolloClient = initializeApollo()

  return isFilterPage
    ? getListingsMapProps({ data, reduxStore, apolloClient })
    : getListingDetailsProps({ data, reduxStore })
}

Component.Layout = makeMainLayout({
  headerConfig,
  footerConfig,
  supportConfig,
  disableZoom: true
})

export default Component
